import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { confirmEmail } from "@/api"
import { LoadingOverlay } from '@/components/auth/loading-overlay';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { useAuth } from "@/context/AuthContext"
import { AuthenticationCheck } from "@/components/auth/AuthenticationCheck"
import { AlertCircle, MailCheck } from 'lucide-react';

const ConfirmEmail = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { setAuthData, isAuthenticated, isLoading: authLoading } = useAuth();
  const { success, error } = useEnhancedToast();

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setIsLoading(false);
        error({
          icon: AlertCircle,
          title: "Invalid Token",
          description: "The email verification link is invalid or has expired.",
          duration: 4000,
        });
        setShouldRedirect(true);
        return;
      }

      try {
        // console.log('Verifying email with token:', token);
        const response = await confirmEmail(token);

        if (response.data) {
          setIsLoading(false);
          await setAuthData();

          success({
            icon: MailCheck,
            title: "Email Verified!",
            description: "Your email has been successfully verified.",
            duration: 5000,
          });
        } else {
          throw new Error(response.error || 'Failed to verify email');
        }
      } catch (err: unknown) {
        // console.error('Error verifying email:', err);
        error({
          icon: AlertCircle,
          title: "Verification Failed",
          description: err instanceof Error ? err.message : 'An error occurred while verifying your email. Please try again.',
          duration: 4000,
        });
        setShouldRedirect(true);
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams]);

  // Show loading state while checking authentication
  if (authLoading) {
    return <AuthenticationCheck />;
  }

  // Redirect authenticated users
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect on error
  if (shouldRedirect) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {isLoading && <LoadingOverlay
        show={isLoading}
        type="email_confirmation"
      />}
    </>
  );
};

export default ConfirmEmail;
